import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Color } from '../../../assets/color';
import { useNavigate } from 'react-router-dom';
import RouteHeader from '../../../components/common/RouteHeader';
import * as SuperadminActions from '../../../redux/actions/superadminAction';
import Swal from 'sweetalert2';

const BugReportForm = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { searchBugReportsData } = useSelector(state => state?.superadminReducer);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        module: '',
        priority: 'Medium',
        error_occurred_at: new Date().toISOString().slice(0, 16),
        affected_user: '',
        affected_user_id: '',
        steps_to_reproduce: '',
        expected_result: '',
        actual_result: '',
        error_message: '',
        browser_info: '',
        device_info: '',
        url_where_error: '',
        related_bugs: [],
    });

    const [relatedSearch, setRelatedSearch] = useState('');
    const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);

    const modules = ['Eye Scan', 'Reports', 'Payment Gateway', 'Partners', 'Affiliates', 'PDF Reports', 'Plans', 'Admin Panel', 'User Management', 'Authentication', 'Dashboard', 'Other'];

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleRelatedSearch = (value) => {
        setRelatedSearch(value);
        if (value.length >= 2) {
            dispatch(SuperadminActions.searchBugReports(value));
            setShowRelatedDropdown(true);
        } else {
            setShowRelatedDropdown(false);
        }
    };

    const addRelatedBug = (bug) => {
        if (!formData.related_bugs.find(b => b._id === bug._id)) {
            setFormData(prev => ({ ...prev, related_bugs: [...prev.related_bugs, bug] }));
        }
        setRelatedSearch('');
        setShowRelatedDropdown(false);
    };

    const removeRelatedBug = (bugId) => {
        setFormData(prev => ({ ...prev, related_bugs: prev.related_bugs.filter(b => b._id !== bugId) }));
    };

    const handleSubmit = () => {
        if (!formData.title || !formData.description || !formData.module || !formData.steps_to_reproduce || !formData.expected_result || !formData.actual_result) {
            Swal.fire({ icon: 'warning', text: 'Please fill all required fields.', showConfirmButton: false, timer: 3000 });
            return;
        }

        const submitData = {
            ...formData,
            related_bugs: formData.related_bugs.map(b => b._id),
        };

        dispatch(SuperadminActions.createBugReport({
            data: submitData,
            onComplete: () => navigate('/bug-tracker/issues'),
        }));
    };

    const inputStyle = {
        width: '100%', padding: '12px 16px', borderRadius: '8px', border: '2px solid #E2E8F0',
        fontSize: '14px', outline: 'none', transition: 'border-color 0.3s', fontFamily: 'inherit',
    };

    const labelStyle = {
        fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block',
    };

    const requiredStar = <span style={{ color: '#EF4444' }}>*</span>;

    const statusColors = {
        'Open': { bg: '#FEF3C7', color: '#D97706' },
        'In Review': { bg: '#EDE9FE', color: '#7C3AED' },
        'Valid': { bg: '#DBEAFE', color: '#2563EB' },
        'In Progress': { bg: '#CFFAFE', color: '#0891B2' },
        'Testing': { bg: '#E0E7FF', color: '#4F46E5' },
        'Closed': { bg: '#D1FAE5', color: '#059669' },
        'Rejected': { bg: '#FEE2E2', color: '#DC2626' },
    };

    return (
        <>
            <RouteHeader title="Report New Bug" sub_title="Submit a detailed bug report for tracking and resolution" />

            <div style={{ backgroundColor: Color.white, padding: '30px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <Grid container spacing={3}>
                    {/* Title */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <label style={labelStyle}>Bug Title {requiredStar}</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="Short, descriptive title of the issue..."
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            onFocus={(e) => e.target.style.borderColor = Color.primary}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />
                    </Grid>

                    {/* Module & Priority */}
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                        <label style={labelStyle}>Module / Area {requiredStar}</label>
                        <select
                            style={{ ...inputStyle, cursor: 'pointer' }}
                            value={formData.module}
                            onChange={(e) => handleChange('module', e.target.value)}
                        >
                            <option value="">Select Module...</option>
                            {modules.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                        <label style={labelStyle}>Priority {requiredStar}</label>
                        <select
                            style={{ ...inputStyle, cursor: 'pointer' }}
                            value={formData.priority}
                            onChange={(e) => handleChange('priority', e.target.value)}
                        >
                            <option value="Critical">🔴 Critical</option>
                            <option value="High">🟠 High</option>
                            <option value="Medium">🔵 Medium</option>
                            <option value="Low">🟢 Low</option>
                        </select>
                    </Grid>

                    {/* Description */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <label style={labelStyle}>Detailed Description {requiredStar}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                            placeholder="Describe the bug in detail. What happened? What were you doing?"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            onFocus={(e) => e.target.style.borderColor = Color.primary}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />
                    </Grid>

                    {/* When & Who */}
                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>When did the error occur?</label>
                        <input
                            style={inputStyle}
                            type="datetime-local"
                            value={formData.error_occurred_at}
                            onChange={(e) => handleChange('error_occurred_at', e.target.value)}
                        />
                    </Grid>

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>Affected User (Name/Email)</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="Which user faced this issue?"
                            value={formData.affected_user}
                            onChange={(e) => handleChange('affected_user', e.target.value)}
                        />
                    </Grid>

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>Affected User ID</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="User ID if available..."
                            value={formData.affected_user_id}
                            onChange={(e) => handleChange('affected_user_id', e.target.value)}
                        />
                    </Grid>

                    {/* Steps to Reproduce */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <label style={labelStyle}>Steps to Reproduce {requiredStar}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            placeholder={"Step 1: Go to ...\nStep 2: Click on ...\nStep 3: Enter ...\nStep 4: Error appears..."}
                            value={formData.steps_to_reproduce}
                            onChange={(e) => handleChange('steps_to_reproduce', e.target.value)}
                            onFocus={(e) => e.target.style.borderColor = Color.primary}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />
                    </Grid>

                    {/* Expected vs Actual */}
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                        <label style={labelStyle}>Expected Result {requiredStar}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                            placeholder="What should have happened?"
                            value={formData.expected_result}
                            onChange={(e) => handleChange('expected_result', e.target.value)}
                            onFocus={(e) => e.target.style.borderColor = Color.primary}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                        <label style={labelStyle}>Actual Result {requiredStar}</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                            placeholder="What is currently happening instead?"
                            value={formData.actual_result}
                            onChange={(e) => handleChange('actual_result', e.target.value)}
                            onFocus={(e) => e.target.style.borderColor = Color.primary}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />
                    </Grid>

                    {/* Error Message */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <label style={labelStyle}>Error Message (if any)</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#1E293B', color: '#F1F5F9', border: '2px solid #334155' }}
                            placeholder="Paste the exact error message here..."
                            value={formData.error_message}
                            onChange={(e) => handleChange('error_message', e.target.value)}
                        />
                    </Grid>

                    {/* URL, Browser, Device */}
                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>URL Where Error Occurred</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="https://web.irisanalysis.ai/..."
                            value={formData.url_where_error}
                            onChange={(e) => handleChange('url_where_error', e.target.value)}
                        />
                    </Grid>

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>Browser Info</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="Chrome 120, Safari 17, etc."
                            value={formData.browser_info}
                            onChange={(e) => handleChange('browser_info', e.target.value)}
                        />
                    </Grid>

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                        <label style={labelStyle}>Device Info</label>
                        <input
                            style={inputStyle}
                            type="text"
                            placeholder="iPhone 15, Samsung S24, Desktop, etc."
                            value={formData.device_info}
                            onChange={(e) => handleChange('device_info', e.target.value)}
                        />
                    </Grid>

                    {/* Related Issues */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <label style={labelStyle}>Related Issues (Link to previous similar bugs)</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                style={inputStyle}
                                type="text"
                                placeholder="Search by bug ID or title to link related issues..."
                                value={relatedSearch}
                                onChange={(e) => handleRelatedSearch(e.target.value)}
                                onBlur={() => setTimeout(() => setShowRelatedDropdown(false), 200)}
                            />
                            {showRelatedDropdown && searchBugReportsData?.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                                    backgroundColor: '#FFF', border: '2px solid #E2E8F0', borderRadius: '8px',
                                    maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                }}>
                                    {searchBugReportsData.map(bug => (
                                        <div key={bug._id}
                                            onClick={() => addRelatedBug(bug)}
                                            style={{
                                                padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F0F0FF'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FFF'}
                                        >
                                            <div>
                                                <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#5045E5', fontWeight: '600' }}>{bug.bug_id}</span>
                                                <span style={{ fontSize: '13px', marginLeft: '8px' }}>{bug.title}</span>
                                            </div>
                                            <span style={{
                                                fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                                                backgroundColor: statusColors[bug.status]?.bg || '#F1F5F9',
                                                color: statusColors[bug.status]?.color || '#64748B',
                                            }}>{bug.status}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Selected Related Bugs */}
                        {formData.related_bugs.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                {formData.related_bugs.map(bug => (
                                    <div key={bug._id} style={{
                                        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                                        backgroundColor: '#EDE9FF', borderRadius: '20px', fontSize: '13px',
                                    }}>
                                        <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#5045E5' }}>{bug.bug_id}</span>
                                        <span>{bug.title?.substring(0, 30)}{bug.title?.length > 30 ? '...' : ''}</span>
                                        <span onClick={() => removeRelatedBug(bug._id)} style={{ cursor: 'pointer', color: '#EF4444', fontWeight: '700', marginLeft: '4px' }}>×</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Grid>

                    {/* Submit Button */}
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <div onClick={() => navigate('/bug-tracker/issues')} style={{
                                padding: '12px 30px', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500',
                                backgroundColor: '#F1F5F9', color: '#64748B',
                            }}>
                                Cancel
                            </div>
                            <div onClick={handleSubmit} style={{
                                padding: '12px 30px', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500',
                                background: 'linear-gradient(to right, #6653E8, #9544EB)', color: '#FFF',
                            }}>
                                Submit Bug Report
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

export default BugReportForm;
